import { normalizeRateRouteId } from '@/utils/rateRouteId';

const DETAILS_ROUTE_NAMES = new Set(['room details', 'rate details']);
const ROUTES_WITH_ROOM_RATE_ID = new Set(['extras', ...DETAILS_ROUTE_NAMES]);

const SESSION_KEY = 'agentEntryRoomRateContext';

/** @typedef {{ roomRateId: string, detailsView: 'room' | 'rate' | null } | null} AgentEntryRoomRateContext */

let pendingEntryContext = null;

export function setPendingAgentEntryContext(context) {
  pendingEntryContext = context;
  try {
    if (context?.roomRateId) {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(context));
    } else {
      sessionStorage.removeItem(SESSION_KEY);
    }
  } catch {
    // ignore
  }
}

export function consumePendingAgentEntryContext() {
  let context = null;

  if (pendingEntryContext?.roomRateId) {
    context = pendingEntryContext;
    pendingEntryContext = null;
  }

  try {
    if (!context) {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.roomRateId) {
          context = {
            roomRateId: normalizeRateRouteId(String(parsed.roomRateId)),
            detailsView: parsed.detailsView ?? null,
          };
        }
      }
    }
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }

  return context;
}

/**
 * @param {import('vue-router').RouteLocationNormalizedLoaded} route
 * @param {{ reservations?: Record<string, unknown> }} [reservationStore]
 * @returns {AgentEntryRoomRateContext}
 */
export function resolveAgentEntryRoomRateContext(
  route,
  reservationStore = null,
) {
  const routeName = route?.name;
  const paramId = route?.params?.id;

  if (ROUTES_WITH_ROOM_RATE_ID.has(routeName) && paramId) {
    return {
      roomRateId: normalizeRateRouteId(String(paramId)),
      detailsView: DETAILS_ROUTE_NAMES.has(routeName)
        ? routeName === 'room details'
          ? 'room'
          : 'rate'
        : null,
    };
  }

  if (routeName === 'guest details' && reservationStore) {
    const reservation = Object.values(reservationStore.reservations ?? {})[0];
    const roomRateCode =
      reservation?.occupancy?.room_rate_code ??
      reservation?.mealPlan?.room_rate_code ??
      reservation?.room_rate_code;
    if (roomRateCode) {
      return {
        roomRateId: normalizeRateRouteId(String(roomRateCode)),
        detailsView: null,
      };
    }
  }

  return null;
}

export function readAgentEntryContextFromHistoryState() {
  if (typeof history === 'undefined') return null;
  const roomRateId = history.state?.entryRoomRateId;
  if (!roomRateId) return null;
  return {
    roomRateId: normalizeRateRouteId(String(roomRateId)),
    detailsView: history.state?.entryDetailsView ?? null,
  };
}

export function appendRoomRateToBookingContextPayload(payload, agentFlowStore) {
  const rawRoomRateId = agentFlowStore.entryRoomRateId;
  if (!rawRoomRateId) return payload;

  const id = normalizeRateRouteId(String(rawRoomRateId));
  const parts = id.split('.');
  // detailsView wins over a composite id left over from mid-chat selection:
  // rate/room entry is always a browse filter, never a concrete offer seed.
  if (agentFlowStore.entryDetailsView === 'rate') {
    payload.rate_plan_code = parts[0] || id;
    return payload;
  }
  if (agentFlowStore.entryDetailsView === 'room') {
    payload.room_type_code = parts.length === 2 ? parts[1] : id;
    return payload;
  }
  if (parts.length === 2) {
    payload.room_rate_code = id;
    payload.rate_plan_code = parts[0];
    payload.room_type_code = parts[1];
  } else {
    payload.rate_plan_code = id;
  }

  return payload;
}
